import { useCallback, useEffect, useMemo, useRef, useState, type SyntheticEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowUpRight, Plus, Search } from 'lucide-react';
import '../landing/editor.css';
import { useAuth } from '../auth';
import { formatDate, formatDateTime } from '../api';
import { EmptyState, ErrorState } from '../components/Ui';
import FilterPill from '../components/FilterPill';
import {
  bestPublicUrl, deletePage, duplicatePage, ensurePreviewToken, listDomains, listPages, pagesOverview,
  type LandingPage, type PageDomain, type PageOverviewRow,
} from '../landing/api';
import { pageConversionRate } from '../landing/model/pages';
import type { PreviewForm } from '../landing/render/FormMock';
import { FeedbackProvider, useConfirm, useToast } from '../landing/ui/feedback';
import { CopyIcon, ExternalIcon, EyeIcon, LinkIcon, ShareIcon, TrashIcon } from '../landing/icons';
import { EditorMenu } from '../landing/editor/EditorMenu';
import { embedCode } from '../landing/editor/ShareDrawer';
import NewPageModal from '../landing/editor/NewPageModal';
import TemplatePreview from '../landing/preview/TemplatePreview';
import TemplateThumb from '../landing/preview/TemplateThumb';

// Pages (Agency, Pages tab): every landing page as a card with a live preview
// of the page, its status, link and last 30 days of traffic, like Orbit's
// list. A card opens the editor (/pages/:id); New page opens the full-screen
// template gallery. The header, empty and error states are NL-AOS's own; the
// toolbar and the cards are Orbit's (Tailwind inside .pe-scope, editor.css).

type Sort = 'updated' | 'name' | 'status';
type StatusFilter = 'all' | 'published' | 'draft';

/** "just now" / "38m ago" / "6h ago" / "3d ago", then the date (Eastern). */
export function updatedAgo(iso: string | null | undefined, now = Date.now()): string {
  if (!iso) return '';
  const t = Date.parse(iso);
  if (!Number.isFinite(t)) return '';
  const m = Math.floor((now - t) / 60000);
  if (m < 1) return 'just now';
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  if (d < 7) return `${d}d ago`;
  return formatDate(iso);
}

const count = (n: number, word: string) => `${n.toLocaleString('en-US')} ${word}${n === 1 ? '' : 's'}`;

/** The card's traffic line (last 30 days): "120 views, 6 leads, 5% converted", or why there is none. */
export function trafficLine(page: Pick<LandingPage, 'status'>, ov: PageOverviewRow | undefined): string {
  if (ov && (ov.views > 0 || ov.leads > 0)) {
    return `${count(ov.views, 'view')}, ${count(ov.leads, 'lead')}, ${pageConversionRate(ov.leads, ov.visitors)}% converted`;
  }
  return page.status === 'published' ? 'No visits yet' : 'Not published';
}

/** Sorts a copy: newest change first, by name, or live pages first. */
export function sortPages(list: LandingPage[], sort: Sort): LandingPage[] {
  const byName = (a: LandingPage, b: LandingPage) => a.name.localeCompare(b.name, 'en', { sensitivity: 'base' });
  return [...list].sort((a, b) => (
    sort === 'name' ? byName(a, b)
      : sort === 'status' ? (a.status === b.status ? byName(a, b) : a.status === 'published' ? -1 : 1)
        : (b.updated_at ?? '').localeCompare(a.updated_at ?? '')
  ));
}

// Card thumbnails never load a live form: a page with a form shows a still of
// one (the CRM contact screen), a page without one shows no form card.
const FORM_STILL: PreviewForm = { fields: ['full_name', 'email'] };

const stop = (e: SyntheticEvent) => e.stopPropagation();

// The card's small actions: Orbit's 32px icon buttons, 44px on touch screens.
const ACT = 'icon-btn disabled:opacity-40 [@media(pointer:coarse)]:h-11 [@media(pointer:coarse)]:w-11';

async function copyText(text: string): Promise<boolean> {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    return false;
  }
}

export default function Pages() {
  return (
    <FeedbackProvider>
      <PagesList />
    </FeedbackProvider>
  );
}

function PagesList() {
  const nav = useNavigate();
  const { user } = useAuth();
  const canWrite = user?.role === 'superadmin' || user?.role === 'exec';
  const toast = useToast();
  const confirm = useConfirm();

  const [pages, setPages] = useState<LandingPage[] | null>(null);
  const [overview, setOverview] = useState<Record<string, PageOverviewRow>>({});
  const [trafficStatus, setTrafficStatus] = useState<'loading' | 'ready' | 'error'>('loading');
  const [domains, setDomains] = useState<PageDomain[]>([]);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [busyAction, setBusyAction] = useState<'duplicate' | 'delete'>('duplicate');
  const [previewId, setPreviewId] = useState<string | null>(null);
  const [previewLink, setPreviewLink] = useState<{ name: string; url: string } | null>(null);
  const actionRef = useRef(false);
  const previewRef = useRef(false);
  const [q, setQ] = useState('');
  const [status, setStatus] = useState<StatusFilter>('all');
  const [sort, setSort] = useState<Sort>('updated');
  const [creating, setCreating] = useState(false);
  const createTrigger = useRef<HTMLButtonElement | null>(null);
  const alive = useRef(true);
  const trafficRequest = useRef(0);
  const listRequest = useRef(0);
  // Set again on mount: StrictMode mounts twice in development.
  useEffect(() => { alive.current = true; return () => { alive.current = false; trafficRequest.current++; listRequest.current++; }; }, []);

  const loadTraffic = useCallback(async () => {
    if (!alive.current) return;
    const request = ++trafficRequest.current;
    setTrafficStatus('loading');
    try {
      const rows = await pagesOverview(30);
      if (alive.current && request === trafficRequest.current) {
        setOverview(Object.fromEntries(rows.map((r) => [r.page_id, r])));
        setTrafficStatus('ready');
      }
    } catch {
      if (alive.current && request === trafficRequest.current) setTrafficStatus('error');
    }
  }, []);

  const load = useCallback(async () => {
    if (!alive.current) return;
    const request = ++listRequest.current;
    setError('');
    // These requests are independent. A slow page list must not delay the
    // traffic or public-domain addresses that the cards will need.
    void loadTraffic();
    void listDomains()
      .then((r) => { if (alive.current && request === listRequest.current) setDomains(r.domains ?? []); })
      .catch(() => {});
    try {
      const list = await listPages();
      if (alive.current && request === listRequest.current) setPages(list);
    } catch (e) {
      if (alive.current && request === listRequest.current) setError((e as Error)?.message || 'The pages did not load.');
    } finally {
      if (alive.current && request === listRequest.current) setLoading(false);
    }
  }, [loadTraffic]);
  useEffect(() => { void load(); }, [load]);

  const closeCreate = useCallback(() => {
    setCreating(false);
    if (createTrigger.current?.isConnected) createTrigger.current.focus({ preventScroll: true });
  }, []);

  // Pages are shared by their live custom-domain address when there is one
  // (the domain's homepage at /, other pages at /<slug>), else by /p/<slug>.
  const homeOf = (p: LandingPage) => domains.find((d) => d.status === 'active' && d.root_page_id === p.id) ?? null;
  const linkOf = (p: LandingPage) => bestPublicUrl(p, domains);
  const linkText = (p: LandingPage) => {
    const url = linkOf(p);
    return url.startsWith('https://') && !url.includes('/p/') ? url.replace(/^https:\/\//, '').replace(/\/$/, '') : `/p/${p.slug}`;
  };

  const term = q.trim().toLowerCase();
  const liveCount = pages?.filter((p) => p.status === 'published').length ?? 0;
  const filters: { value: StatusFilter; label: string; count: number }[] = [
    { value: 'all', label: 'All pages', count: pages?.length ?? 0 },
    { value: 'published', label: 'Live', count: liveCount },
    { value: 'draft', label: 'Drafts', count: (pages?.length ?? 0) - liveCount },
  ];
  const shown = useMemo(() => {
    const all = (pages ?? []).filter((p) => status === 'all' || p.status === status);
    const hit = term
      ? all.filter((p) => p.name.toLowerCase().includes(term) || p.slug.toLowerCase().includes(term)
        || bestPublicUrl(p, domains).toLowerCase().includes(term))
      : all;
    return sortPages(hit, sort);
  }, [pages, term, sort, status, domains]);

  // ── Card actions ─────────────────────────────────────────────────────
  // Opens the page as it is right now (draft or live) through its private
  // ?pt= link. The tab opens during the click (popup blockers allow that) and
  // gets its address once the token is back.
  async function preview(p: LandingPage) {
    if (previewRef.current) return;
    const link = (token: string) => `${window.location.origin}/p/${encodeURIComponent(p.slug)}?pt=${encodeURIComponent(token)}`;
    previewRef.current = true;
    setPreviewId(p.id);
    setPreviewLink(null);
    const tab = window.open('', '_blank');
    if (tab) tab.opener = null;
    try {
      const token = p.preview_token || (await ensurePreviewToken(p.id)).token;
      if (alive.current) setPages((cur) => cur?.map((x) => (x.id === p.id ? { ...x, preview_token: token } : x)) ?? cur);
      if (tab) {
        // Closing the pending tab is a cancellation, not a reason to reopen it.
        if (!tab.closed) tab.location.href = link(token);
      } else if (alive.current) {
        // A second window.open after awaiting the token is usually blocked too.
        // A real link lets the person open their preview directly.
        setPreviewLink({ name: p.name, url: link(token) });
      }
    } catch (e) {
      tab?.close();
      if (alive.current) toast((e as Error)?.message || 'Couldn’t open the preview. Try again.', 'error');
    } finally {
      previewRef.current = false;
      if (alive.current) setPreviewId(null);
    }
  }

  // No confirm: a copy is a new draft, and it opens right away.
  async function duplicate(p: LandingPage) {
    if (actionRef.current) return;
    actionRef.current = true;
    setBusyAction('duplicate');
    setBusyId(p.id);
    try {
      const copy = await duplicatePage(p.id);
      // The copy still exists if they moved on, but it must not interrupt a
      // different editor (which may now contain unsaved work).
      if (alive.current) nav(`/pages/${copy.id}`);
    } catch (e) {
      if (alive.current) toast((e as Error)?.message || 'Couldn’t duplicate the page. Try again.', 'error');
    } finally {
      actionRef.current = false;
      if (alive.current) setBusyId(null);
    }
  }

  async function remove(p: LandingPage) {
    if (actionRef.current) return;
    actionRef.current = true;
    const home = domains.find((d) => d.root_page_id === p.id);
    const ok = await confirm({
      title: `Delete “${p.name}”?`,
      body: home
        ? `Its links stop working and ${home.domain} loses its homepage. This cannot be undone.`
        : 'Its link stops working. This cannot be undone.',
      confirmLabel: 'Delete page',
      danger: true,
    });
    if (!ok || !alive.current) { actionRef.current = false; return; }
    setBusyAction('delete');
    setBusyId(p.id);
    try {
      await deletePage(p.id);
      if (!alive.current) return;
      setPages((cur) => cur?.filter((x) => x.id !== p.id) ?? cur);
      setPreviewLink(null);
      toast('Page deleted');
      void load();
    } catch (e) {
      if (alive.current) toast((e as Error)?.message || 'Couldn’t delete the page. Try again.', 'error');
    } finally {
      actionRef.current = false;
      if (alive.current) setBusyId(null);
    }
  }

  async function copy(text: string, what: string) {
    if (await copyText(text)) toast(`${what} copied`);
    else toast('Couldn’t copy. Select it and copy it yourself.', 'error');
  }

  const newPage = canWrite && (
    <button type="button" className="button" disabled={busyId !== null} onClick={(event) => { if (!actionRef.current) { createTrigger.current = event.currentTarget; setCreating(true); } }}><Plus aria-hidden="true" />New page</button>
  );

  return (
    <div className="pages-screen">
      {/* NL-AOS's page header. The count lives on the All pages tab only. */}
      <div className="page-header pages-header">
        <div>
          <h1>Pages</h1>
        </div>
        {newPage && <div className="page-actions">{newPage}</div>}
      </div>

      {pages && error && (
        <div className="pages-traffic-error" role="alert">
          <span>Pages couldn’t refresh. {error}</span>
          <button type="button" onClick={() => { void load(); }}>Retry pages</button>
        </div>
      )}

      {loading && !pages ? (
        <div className="pe-scope">
          <div role="status" aria-label="Loading pages" className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4">
            {Array.from({ length: 6 }, (_, i) => (
              <div key={i} className="overflow-hidden rounded-2xl border border-border bg-surface">
                <i className="skeleton-line h-[210px] rounded-none" />
                <div className="grid gap-2 px-4 pb-4 pt-3.5">
                  <i className="skeleton-line h-3.5 w-1/2" />
                  <i className="skeleton-line h-3 w-1/3" />
                  <i className="skeleton-line h-3 w-2/3" />
                </div>
              </div>
            ))}
          </div>
        </div>
      ) : !pages ? (
        <ErrorState message={error || 'The pages did not load.'} onRetry={() => { setLoading(true); void load(); }} />
      ) : pages.length === 0 ? (
        <section className="panel">
          <EmptyState title="No pages yet"
            description={canWrite ? 'Start from a template or a blank page.' : 'Pages your team builds show up here.'}
            action={newPage || undefined} />
        </section>
      ) : (
        <div className="pe-scope">
          <div className="pages-toolbar">
            <div className="pages-filters" role="group" aria-label="Filter pages by status">
              {filters.map((f) => (
                <button key={f.value} type="button" aria-pressed={status === f.value} onClick={() => setStatus(f.value)}>
                  {f.label}<span>{f.count}</span>
                </button>
              ))}
            </div>
            <div className="pages-search-sort">
              <label className="pages-search">
                <Search size={15} aria-hidden="true" />
                <input type="search" className="input" placeholder="Search pages"
                  aria-label="Search pages" value={q} onChange={(e) => setQ(e.target.value)} />
              </label>
              <FilterPill label="Sort" value={sort} onChange={(value) => setSort(value as Sort)} options={[
                { value: 'updated', name: 'Recently updated' },
                { value: 'name', name: 'Name A–Z' },
                { value: 'status', name: 'Live first' },
              ]} />
            </div>
          </div>
          <div className="pages-list-meta">
            <span>Traffic from the last 30 days</span>
            {/* A count only while search or a tab narrows the list; the tab already counts them all. */}
            <span role="status">{shown.length === pages.length ? '' : `${shown.length} of ${count(pages.length, 'page')}`}</span>
          </div>
          {previewLink && (
            <div className="pages-traffic-error" role="status">
              <span>Your browser blocked the preview window.</span>
              <a href={previewLink.url} target="_blank" rel="noopener noreferrer">Open {previewLink.name} preview</a>
            </div>
          )}
          {trafficStatus === 'error' && (
            <div className="pages-traffic-error" role="status">
              <span>Traffic couldn’t load. Your pages are still available.</span>
              <button type="button" onClick={() => { void loadTraffic(); }}>Retry traffic</button>
            </div>
          )}
          {shown.length === 0 ? (
            <div className="pages-no-results">
              <Search size={22} aria-hidden="true" />
              <h2>{term ? `No pages match “${q.trim()}”.` : status === 'published' ? 'No live pages yet' : 'No drafts right now'}</h2>
              <p>{term ? 'Try a different name or link, or clear your filters.' : status === 'published' ? 'Open a draft to review and publish it.' : 'Your next landing page starts with a template.'}</p>
              <button type="button" className="btn-ghost" onClick={() => { setQ(''); setStatus('all'); }}>Clear filters</button>
            </div>
          ) : (
            <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4">
              {shown.map((p) => {
                const live = p.status === 'published';
                const steps = p.content?.steps?.length ?? 0;
                const url = linkOf(p);
                const home = homeOf(p);
                const open = () => { if (busyId !== p.id) nav(`/pages/${p.id}`); };
                return (
                  <div key={p.id} role="link" tabIndex={0} aria-label={`Open ${p.name}`} data-page-card={p.id}
                    onClick={open} onKeyDown={(e) => { if (e.key === 'Enter' && e.target === e.currentTarget) open(); }}
                    className="pages-card group flex min-w-0 cursor-pointer flex-col overflow-hidden rounded-2xl border border-border bg-surface transition-colors hover:border-borderHi" aria-busy={busyId === p.id || undefined}>
                    <div className="border-b border-border">
                      <TemplatePreview content={p.content ?? {}} theme={p.theme ?? {}} height={210} flush title={`${p.name} preview`}
                        previewForm={p.form_id ? FORM_STILL : null}
                        fallback={<TemplateThumb content={p.content ?? {}} theme={p.theme ?? {}} />} />
                    </div>
                    <div className="flex flex-1 flex-col px-4 pb-3 pt-3.5">
                      <div className="flex items-start justify-between gap-3">
                        <h3 className="min-w-0 truncate text-base font-semibold text-text" title={p.name}>{p.name}</h3>
                        <span className={`flex shrink-0 items-center gap-1.5 pt-0.5 text-xs ${live ? 'text-success' : 'text-textDim'}`}>
                          <span className={`h-1.5 w-1.5 rounded-full ${live ? 'bg-success' : 'bg-textDim/50'}`} />
                          {live ? 'Live' : 'Draft'}
                        </span>
                      </div>
                      <p className="mt-0.5 truncate text-xs text-textDim" title={home ? `Homepage of ${home.domain}` : undefined}>
                        {linkText(p)}{steps > 1 ? `, ${steps} steps` : ''}
                      </p>
                      <p className="pages-card-traffic mt-1 text-sm text-textDim tnum" title="Last 30 days">
                        {trafficStatus === 'ready' ? trafficLine(p, overview[p.id]) : live ? trafficStatus === 'loading' ? 'Loading traffic…' : 'Traffic unavailable' : 'Not published'}
                      </p>
                      <div className="mt-3 flex items-center justify-between gap-2 border-t border-border pt-2.5" onClick={stop} onKeyDown={stop}>
                        <span className="truncate text-xs text-textDim" title={formatDateTime(p.updated_at)}>Updated {updatedAgo(p.updated_at)}</span>
                        <div className="flex shrink-0 items-center gap-0.5">
                          <button type="button" className={ACT} aria-label={`Preview ${p.name}`} title={previewId === p.id ? 'Opening preview…' : 'Preview'} disabled={previewId !== null} aria-busy={previewId === p.id || undefined} onClick={() => { void preview(p); }}>
                            {previewId === p.id ? <span className="pe-spinner" aria-hidden="true" /> : <EyeIcon size={16} />}
                          </button>
                          {live ? (
                            <EditorMenu label={`Share ${p.name}`} title="Share" buttonClassName={ACT} icon={<ShareIcon size={16} />} width={190} items={[
                              { label: 'Copy link', icon: <LinkIcon size={15} />, onSelect: () => { void copy(url, 'Link'); } },
                              { label: 'Copy embed code', icon: <CopyIcon size={15} />, onSelect: () => { void copy(embedCode(url, p.name), 'Embed code'); } },
                              { label: 'Open', icon: <ExternalIcon size={15} />, onSelect: () => { window.open(url, '_blank', 'noopener'); } },
                            ]} />
                          ) : (
                            <button type="button" className={ACT} aria-label={`Share ${p.name}`} title="Publish first to share" disabled>
                              <ShareIcon size={16} />
                            </button>
                          )}
                          {canWrite && (
                            <button type="button" className={ACT} aria-label={`Duplicate ${p.name}`} title="Duplicate" disabled={busyId !== null}
                              onClick={() => { void duplicate(p); }}>
                              <CopyIcon size={15} />
                            </button>
                          )}
                          {canWrite && (
                            <button type="button" className={`${ACT} icon-btn-danger`} aria-label={`Delete ${p.name}`} title="Delete" disabled={busyId !== null}
                              onClick={() => { void remove(p); }}>
                              <TrashIcon size={15} />
                            </button>
                          )}
                        </div>
                      </div>
                      <span className="pages-card-open">{busyId === p.id ? <><span className="pe-spinner" aria-hidden="true" />{busyAction === 'delete' ? 'Deleting…' : 'Creating copy…'}</> : <>{canWrite ? 'Edit page' : 'View page'}<ArrowUpRight size={13} aria-hidden="true" /></>}</span>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {canWrite && creating && <NewPageModal open onClose={closeCreate} />}
    </div>
  );
}
